import React, { useEffect, useMemo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  TrashIcon,
  LocationMarkerIcon,
} from '@heroicons/react/outline';

import InputPresentational from '../Form/InputPresentational';
import SelectPresentational from '../Form/SelectPresentational';
import TextareaPresentational from '../Form/TextAreaPresentational';
import CheckboxPresentational from '../Form/SimpleCheckboxPresentational';

import { getFidelityCategories } from '../../api/fidelity';
import sectionsAPIs from '../../api/section';
import { FidelityRequest } from '../../interfaces/fidelity';
import { DISCOUNT_VALUES } from '../../constants/fidelity';
import { isDelegato } from '../../libs/utils/auth';
import { geocodeAddress } from '../../libs/utils/geocoding';

interface FidelityFormProps {
  register: UseFormRegister<FidelityRequest>;
  control: Control<FidelityRequest>;
  errors: FieldErrors<FidelityRequest>;
  watch: UseFormWatch<FidelityRequest>;
  setValue: UseFormSetValue<FidelityRequest>;
  token: string | null;
  profile: any;
  isReadOnly?: boolean;
  isEditMode?: boolean;
  tesseraWarning?: string | null;
}

const FidelityForm: React.FC<FidelityFormProps> = ({
  register,
  control,
  errors,
  watch,
  setValue,
  token,
  profile,
  isReadOnly = false,
  isEditMode = false,
  tesseraWarning = null,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'categorie',
  });

  const socioOnav = watch('socioOnav');
  const indirizzo = watch('indirizzo');
  const citta = watch('citta');
  const provincia = watch('provincia');
  const categorie = watch('categorie');
  const [discountScope, setDiscountScope] = useState<
    'online' | 'negozio' | 'online e negozio'
  >('online e negozio');

  useEffect(() => {
    if (categorie && categorie.length > 0) {
      const first = categorie[0];
      if (first) {
        const online = Number(first.scontoOnline || 0);
        const negozio = Number(first.scontoNegozio || 0);
        if (online > 0 && negozio > 0) setDiscountScope('online e negozio');
        else if (online > 0) setDiscountScope('online');
        else if (negozio > 0) setDiscountScope('negozio');
        else setDiscountScope('online e negozio');
      }
    }
  }, [categorie]);

  const { data: sections } = useQuery(
    ['sections-options', token],
    () =>
      sectionsAPIs.findOptions({
        query: { pagination: { pageSize: 1000 } },
        token,
      }),
    {
      enabled: !isDelegato(profile),
      staleTime: Infinity,
    },
  );

  const { data: categoriesData } = useQuery(
    'fidelityCategories',
    () => getFidelityCategories(),
    {
      staleTime: Infinity,
    },
  );

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalizzazione non supportata dal browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setValue('latitude', Number(latitude.toFixed(6)));
        setValue('longitude', Number(longitude.toFixed(6)));
        toast.success('Posizione ottenuta con successo!');
      },
      (error) => {
        console.error('Errore geolocalizzazione:', error);
        toast.error(
          'Impossibile ottenere la posizione. Verifica i permessi del browser.',
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const getLocationFromAddress = async () => {
    if (!indirizzo || !citta) {
      toast.error(
        "Inserisci almeno l'indirizzo e la città per ottenere le coordinate",
      );
      return;
    }

    try {
      const coordinates = await geocodeAddress({ indirizzo, citta, provincia });

      if (coordinates) {
        setValue('latitude', coordinates.latitude);
        setValue('longitude', coordinates.longitude);
        toast.success("Coordinate ottenute dall'indirizzo!");
      } else {
        toast.error('Indirizzo non trovato. Verifica i dati inseriti.');
      }
    } catch (error) {
      console.error('Errore geocoding:', error);
      toast.error("Errore nella ricerca delle coordinate dall'indirizzo");
    }
  };

  const currentDescriptionLength = watch('descrizione')?.length || 0;

  return (
    <>
      <div className='bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>
              Informazioni Generali
            </h3>
            <p className='mt-1 text-sm text-gray-600'>
              Dati anagrafici e di contatto dell'esercizio
            </p>
          </div>

          <div className='mt-5 md:mt-0 md:col-span-2'>
            <div className='grid grid-cols-6 gap-6'>
              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Nome visualizzato su onav.it*'
                  name='nome'
                  type='text'
                  error={errors.nome}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Ragione Sociale*'
                  name='ragioneSociale'
                  type='text'
                  error={errors.ragioneSociale}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Partita IVA*'
                  placeholder='Es: 12345678901'
                  name='partitaIva'
                  type='text'
                  error={errors.partitaIva}
                  disabled={isReadOnly || isEditMode}
                />
              </div>

              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Email*'
                  type='email'
                  name='email'
                  error={errors.email}
                  disabled={isReadOnly || isEditMode}
                />
              </div>

              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Telefono*'
                  placeholder='+39...'
                  name='telefono'
                  type='text'
                  error={errors.telefono}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-3'>
                <InputPresentational
                  register={register}
                  label='Telefono Secondario'
                  name='telefonoSecondario'
                  type='text'
                  error={errors.telefonoSecondario}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6'>
                <InputPresentational
                  register={register}
                  label='Sito Web'
                  placeholder='https://...'
                  name='sito'
                  type='text'
                  error={errors.sito}
                  disabled={isReadOnly}
                />
              </div>

              {!isDelegato(profile) && (
                <div className='col-span-6 sm:col-span-3'>
                  <SelectPresentational
                    control={control}
                    label='Sezione ONAV*'
                    name='sezione'
                    error={errors.sezione}
                    disabled={isReadOnly}
                    options={
                      sections?.data?.map((section: any) => ({
                        value: section.id,
                        label: section.attributes.name,
                      })) || []
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>
              Indirizzo
            </h3>
            <p className='mt-1 text-sm text-gray-600'>
              Indirizzo dell'esercizio
            </p>
          </div>

          <div className='mt-5 md:mt-0 md:col-span-2'>
            <div className='grid grid-cols-6 gap-6'>
              <div className='col-span-6'>
                <InputPresentational
                  register={register}
                  label='Indirizzo*'
                  name='indirizzo'
                  type='text'
                  error={errors.indirizzo}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-2'>
                <InputPresentational
                  register={register}
                  label='CAP*'
                  maxLength={5}
                  name='cap'
                  type='text'
                  error={errors.cap}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-2'>
                <InputPresentational
                  register={register}
                  label='Città*'
                  name='citta'
                  type='text'
                  error={errors.citta}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6 sm:col-span-2'>
                <InputPresentational
                  register={register}
                  label='Provincia*'
                  maxLength={2}
                  placeholder='MI'
                  name='provincia'
                  type='text'
                  error={errors.provincia}
                  disabled={isReadOnly}
                />
              </div>

              <div className='col-span-6'>
                <div className='flex items-center justify-between mb-2'>
                  <label className='block text-sm font-medium text-gray-700'>
                    Coordinate GPS (facoltativo)
                  </label>
                  {!isReadOnly && (
                    <div className='flex gap-2'>
                      <button
                        type='button'
                        onClick={getLocationFromAddress}
                        className='flex items-center px-3 py-1 text-xs font-medium text-primary hover:text-primary-dark'
                      >
                        <LocationMarkerIcon className='h-3 w-3 mr-1' />
                        Ottieni da indirizzo
                      </button>
                      <button
                        type='button'
                        onClick={getLocation}
                        className='flex items-center px-3 py-1 text-xs font-medium text-primary hover:text-primary-dark'
                      >
                        <LocationMarkerIcon className='h-3 w-3 mr-1' />
                        Posizione attuale
                      </button>
                    </div>
                  )}
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <InputPresentational
                    register={register}
                    label='Latitudine'
                    type='text'
                    name='latitude'
                    placeholder='Es: 45.123456'
                    error={errors.latitude}
                    disabled={isReadOnly}
                  />
                  <InputPresentational
                    register={register}
                    label='Longitudine'
                    type='text'
                    name='longitude'
                    placeholder='Es: 9.123456'
                    error={errors.longitude}
                    disabled={isReadOnly}
                  />
                </div>
                <p className='mt-1 text-sm text-gray-500'>
                  Le coordinate aiutano i soci a trovarti sulla mappa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>
              Categorie e Sconti
            </h3>
            <p className='mt-1 text-sm text-gray-600'>
              Categorie merceologiche e relativi sconti (max 5)
            </p>
          </div>

          <div className='mt-5 md:mt-0 md:col-span-2'>
            <div className='space-y-4'>
              <div className='grid grid-cols-6 gap-4'>
                <div className='col-span-6 sm:col-span-3'>
                  <label className='block text-sm text-gray-500'>
                    Applicazione sconto
                  </label>
                  <select
                    value={discountScope}
                    onChange={(e) => setDiscountScope(e.target.value as any)}
                    className='mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm disabled:opacity-50'
                    disabled={isReadOnly}
                  >
                    <option value='online'>Online</option>
                    <option value='negozio'>In negozio</option>
                    <option value='online e negozio'>
                      Online e in negozio
                    </option>
                  </select>
                </div>
              </div>

              <div>
                <TextareaPresentational
                  register={register}
                  label='Note visibili in mappa (max 120)'
                  id='note'
                  name='note'
                  error={errors.note}
                  disabled={isReadOnly}
                />
              </div>

              {fields.map((field, index) => (
                <div key={field.id} className='bg-gray-50 p-4 rounded-lg'>
                  <div className='flex justify-between items-center mb-4'>
                    <h4 className='text-sm font-medium text-gray-900'>
                      {index === 0
                        ? 'Categoria principale*'
                        : `Categoria ${index + 1}`}
                    </h4>
                    {index > 0 && !isReadOnly && (
                      <button
                        type='button'
                        onClick={() => remove(index)}
                        className='text-red-600 hover:text-red-900'
                      >
                        <TrashIcon className='h-5 w-5' />
                      </button>
                    )}
                  </div>

                  <div className='grid grid-cols-6 gap-4'>
                    <div className='col-span-6 sm:col-span-3'>
                      <SelectPresentational
                        control={control}
                        label='Categoria*'
                        name={`categorie.${index}.categoria`}
                        onChangeValue={() => {
                          setValue(
                            `categorie.${index}.sottocategoria`,
                            undefined,
                          );
                        }}
                        error={errors.categorie?.[index]?.categoria}
                        disabled={isReadOnly}
                        options={
                          categoriesData?.data?.map((cat) => ({
                            value: cat.category,
                            label: cat.category,
                          })) || []
                        }
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <SelectPresentational
                        control={control}
                        label='Sottocategoria'
                        name={`categorie.${index}.sottocategoria`}
                        error={errors.categorie?.[index]?.sottocategoria}
                        options={[
                          { value: '', label: 'Seleziona...' },
                          ...(categorie[index].categoria
                            ? categoriesData?.data
                                .find(
                                  (c) =>
                                    categorie[index].categoria === c.category,
                                )
                                ?.subcategories?.map((sub) => ({
                                  value: sub,
                                  label: sub,
                                })) || []
                            : []),
                        ]}
                        disabled={!categorie[index]?.categoria || isReadOnly}
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <SelectPresentational
                        key={discountScope}
                        control={control}
                        label='Sconto (%)'
                        name={`categorie.${index}.${
                          discountScope === 'online'
                            ? 'scontoOnline'
                            : 'scontoNegozio'
                        }`}
                        error={
                          errors.categorie?.[index]?.[
                            discountScope === 'online'
                              ? 'scontoOnline'
                              : 'scontoNegozio'
                          ]
                        }
                        disabled={isReadOnly}
                        options={[...DISCOUNT_VALUES]}
                        onChangeValue={(opt) => {
                          const value =
                            typeof opt === 'object'
                              ? (opt.value ?? 0)
                              : ((opt as unknown as number) ?? 0);
                          const applyOnline =
                            discountScope === 'online' ||
                            discountScope === 'online e negozio'
                              ? value
                              : 0;
                          const applyNegozio =
                            discountScope === 'negozio' ||
                            discountScope === 'online e negozio'
                              ? value
                              : 0;
                          setValue(
                            `categorie.${index}.scontoOnline`,
                            applyOnline,
                          );
                          setValue(
                            `categorie.${index}.scontoNegozio`,
                            applyNegozio,
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {fields.length < 5 && !isReadOnly && (
                <button
                  type='button'
                  onClick={() =>
                    append({
                      categoria: '',
                      sottocategoria: '',
                      scontoOnline: 0,
                      scontoNegozio: 0,
                    })
                  }
                  className='flex items-center text-primary hover:text-primary-dark'
                >
                  <PlusIcon className='h-5 w-5 mr-1' />
                  Aggiungi categoria
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className='bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6'>
        <div className='md:grid md:grid-cols-3 md:gap-6'>
          <div className='md:col-span-1'>
            <h3 className='text-lg font-medium leading-6 text-gray-900'>
              Dati aggiuntivi
            </h3>
            <p className='mt-1 text-sm text-gray-600'>
              Informazioni aggiuntive sull'esercizio
            </p>
          </div>

          <div className='mt-5 md:mt-0 md:col-span-2'>
            <div className='space-y-6'>
              <div>
                <CheckboxPresentational
                  register={register}
                  id='socioOnav'
                  label="L'azienda è legata a una persona che ha la tessera ONAV?"
                  name='socioOnav'
                  disabled={isReadOnly}
                />
              </div>

              {socioOnav && (
                <div className='grid grid-cols-6 gap-6'>
                  <div className='col-span-6 sm:col-span-3'>
                    <InputPresentational
                      register={register}
                      label='Numero Tessera*'
                      type='number'
                      name='numeroTessera'
                      error={errors.numeroTessera}
                      disabled={isReadOnly}
                    />
                    {tesseraWarning && (
                      <div className='mt-2 p-2 bg-amber-50 border border-amber-300 rounded-md'>
                        <p className='text-sm text-amber-800'>
                          {tesseraWarning}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {socioOnav && (
                <div>
                  <TextareaPresentational
                    register={register}
                    label='Descrizione'
                    id='descrizione'
                    name='descrizione'
                    error={errors.descrizione}
                    disabled={isReadOnly}
                  />
                  <p
                    className={`mt-2 text-sm ${
                      currentDescriptionLength > 600
                        ? 'text-red-600 font-medium'
                        : 'text-gray-500'
                    }`}
                  >
                    {currentDescriptionLength}/600 caratteri
                    {currentDescriptionLength > 600 &&
                      ' - Superato il limite massimo'}
                  </p>
                </div>
              )}

              <div>
                <CheckboxPresentational
                  register={register}
                  id='guidaProsit'
                  label='Presenza su GUIDA PROSIT'
                  name='guidaProsit'
                  disabled={isReadOnly}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FidelityForm;
